import { useState } from 'react';
import type { BandeauDeJournee, ReponseAuBandeau } from '../../partage/api/types';
import { useCocher, useRepondreAuBandeau } from '../journeeDuJour';
import { enMots } from './refusDuJour';
import { decaler, heureLisible, jourEnToutesLettres } from '../../partage/datesEnMots';
import './JourneeDuJour.css';

/**
 * Les deux bandeaux de « Maintenant » (ticket 16 de retours-testeurs, décisions d'Igor du
 * 2026-09-27 ; pas de maquette).
 *
 * - **La veille** d'une journée préparée : « Ta journée de demain est-elle toujours prévue dans
 *   cet ordre ? ». Oui : rien ne change. Non : reporter certaines tâches, la journée entière, ou
 *   les rendre disponibles pour plus tard.
 * - **Le lendemain**, s'il reste des tâches non cochées : « Les tâches d'hier sont-elles
 *   faites ? », une case devant chacune ; les autres se reportent ou redeviennent disponibles.
 *
 * Le serveur décide quand ils se montrent (`domain/maintenant/journee/journeeDuJour.ts`) ; ici, les gestes.
 */

type Temps = 'question' | 'choix' | 'certaines' | 'toutes';

/** Reporter ou rendre disponibles : les trois choix, puis le jour. */
function Reporter({
  bandeau,
  aujourdhui,
  temps,
  setTemps,
  mots,
  onAnnonce,
  retour,
}: {
  bandeau: BandeauDeJournee;
  aujourdhui: string;
  temps: Temps;
  setTemps: (t: Temps) => void;
  mots: { certaines: string; toutes: string };
  onAnnonce: (t: string) => void;
  retour: Temps;
}) {
  const repondre = useRepondreAuBandeau();
  const tous = bandeau.taches.map((t) => t.bulleId);
  const [choisies, setChoisies] = useState<string[]>([]);
  const parDefaut = bandeau.jour >= aujourdhui ? decaler(bandeau.jour, 1) : aujourdhui;
  const [vers, setVers] = useState(parDefaut);
  const [message, setMessage] = useState<string | null>(null);

  function envoyer(reponse: ReponseAuBandeau, texte: string) {
    setMessage(null);
    repondre.mutate({ jour: bandeau.jour, reponse }, { onSuccess: () => onAnnonce(texte), onError: (e) => setMessage(enMots(e, 'bandeau')) });
  }
  const reporter = (taches: string[]) =>
    envoyer(
      { geste: 'reporter', taches, vers },
      `${taches.length > 1 ? 'Reportées' : 'Reportée'} au ${jourEnToutesLettres(vers)}.`
    );

  if (temps === 'choix') {
    return (
      <div className="bandeau-journee-choix">
        <button type="button" className="journee-doux" onClick={() => setTemps('certaines')}>
          {mots.certaines}
        </button>
        <button type="button" className="journee-doux" onClick={() => setTemps('toutes')}>
          {mots.toutes}
        </button>
        <button
          type="button"
          className="journee-doux"
          disabled={repondre.isPending}
          onClick={() => envoyer({ geste: 'liberer', taches: tous }, 'Elles reviennent dans « Maintenant », sans jour.')}
        >
          Les rendre disponibles pour plus tard
        </button>
        {retour !== 'choix' && (
          <button type="button" className="journee-doux journee-doux--tenu" onClick={() => setTemps(retour)}>
            ‹ Retour
          </button>
        )}
        {message && <p className="journee-message" role="status">{message}</p>}
      </div>
    );
  }

  const certaines = temps === 'certaines';
  return (
    <div className="bandeau-journee-choix bandeau-journee-choix--jour">
      {certaines && <p className="journee-sous">Coche celles à reporter&nbsp;:</p>}
      {certaines && (
        <ul className="bandeau-journee-liste">
          {bandeau.taches.map((t) => {
            const on = choisies.includes(t.bulleId);
            return (
              <li key={t.bulleId}>
                <label className="bandeau-journee-case">
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => setChoisies(on ? choisies.filter((id) => id !== t.bulleId) : [...choisies, t.bulleId])}
                  />
                  {t.titre}
                </label>
              </li>
            );
          })}
        </ul>
      )}
      <label className="bandeau-journee-jour">
        Vers le
        <input type="date" value={vers} min={aujourdhui} onChange={(e) => e.target.value && setVers(e.target.value)} />
      </label>
      <button
        type="button"
        className="journee-fort"
        disabled={repondre.isPending || (certaines && choisies.length === 0) || vers === bandeau.jour}
        onClick={() => reporter(certaines ? choisies : tous)}
      >
        {certaines ? `Reporter (${choisies.length})` : 'Reporter'}
      </button>
      <button type="button" className="journee-doux" onClick={() => setTemps(retour)}>
        ‹ Retour
      </button>
      {message && <p className="journee-message" role="status">{message}</p>}
    </div>
  );
}

export function BandeauDeLaVeille({
  bandeau,
  aujourdhui,
  onAnnonce,
}: {
  bandeau: BandeauDeJournee;
  aujourdhui: string;
  onAnnonce: (t: string) => void;
}) {
  const [temps, setTemps] = useState<Temps>('question');
  const repondre = useRepondreAuBandeau();
  return (
    <section className="bandeau-journee" aria-label="Ta journée de demain">
      <p className="journee-question">Ta journée de demain est-elle toujours prévue dans cet ordre&nbsp;?</p>
      <ol className="bandeau-journee-liste">
        {bandeau.taches.map((t) => (
          <li key={t.bulleId}>
            <span className="journee-heure">{heureLisible(t.heure)}</span> {t.titre}
          </li>
        ))}
      </ol>
      {temps === 'question' ? (
        <div className="bandeau-journee-choix">
          <button
            type="button"
            className="journee-fort"
            disabled={repondre.isPending}
            onClick={() =>
              repondre.mutate(
                { jour: bandeau.jour, reponse: { geste: 'oui' } },
                {
                  onSuccess: () => onAnnonce('Ta journée de demain reste comme elle est.'),
                  onError: (e) => onAnnonce(enMots(e, 'bandeau')),
                }
              )
            }
          >
            Oui
          </button>
          <button type="button" className="journee-doux" onClick={() => setTemps('choix')}>
            Non
          </button>
        </div>
      ) : (
        <>
          {temps === 'choix' && <p className="journee-sous">Qu’est-ce qui change&nbsp;?</p>}
          <Reporter
            bandeau={bandeau}
            aujourdhui={aujourdhui}
            temps={temps}
            setTemps={setTemps}
            mots={{ certaines: 'Reporter certaines tâches', toutes: 'Reporter la journée entière' }}
            onAnnonce={onAnnonce}
            retour={temps === 'choix' ? 'question' : 'choix'}
          />
        </>
      )}
    </section>
  );
}

export function BandeauDuLendemain({
  bandeau,
  aujourdhui,
  onAnnonce,
}: {
  bandeau: BandeauDeJournee;
  aujourdhui: string;
  onAnnonce: (t: string) => void;
}) {
  const [temps, setTemps] = useState<Temps>('choix');
  const cocher = useCocher();
  return (
    <section className="bandeau-journee" aria-label="Les tâches d’hier">
      <p className="journee-question">Les tâches d’hier sont-elles faites&nbsp;?</p>
      {temps === 'choix' && <p className="journee-sous">Coche celles que tu as faites.</p>}
      {temps === 'choix' && (
        <ul className="bandeau-journee-liste">
          {bandeau.taches.map((t) => (
            <li key={t.bulleId}>
              <label className="bandeau-journee-case">
                <input
                  type="checkbox"
                  checked={false}
                  disabled={cocher.isPending}
                  onChange={() =>
                    cocher.mutate(
                      { id: t.bulleId, fait: true },
                      {
                        onSuccess: () => onAnnonce(`« ${t.titre} » est faite.`),
                        onError: (e) => onAnnonce(enMots(e, 'bandeau')),
                      }
                    )
                  }
                />
                {t.titre}
              </label>
            </li>
          ))}
        </ul>
      )}
      {temps === 'choix' && <p className="journee-sous">Pour les autres&nbsp;:</p>}
      <Reporter
        bandeau={bandeau}
        aujourdhui={aujourdhui}
        temps={temps}
        setTemps={setTemps}
        mots={{ certaines: 'Reporter certaines à un autre jour', toutes: 'Les reporter toutes à un autre jour' }}
        onAnnonce={onAnnonce}
        retour="choix"
      />
    </section>
  );
}
